/**
 * Print API Service
 * Handles print pricing and quote update API calls
 */
import * as Sentry from '@sentry/browser';
import { formatCurrency } from '../../utils/format-utils.js';

const API_BASE = import.meta.env.VITE_API_GATEWAY_URL;

/**
 * @typedef {Object} PrintSpecs
 * @property {string} paperSize - Paper size (e.g., "A5")
 * @property {string} pagination - "Double Sided" or "Single Sided"
 * @property {string} colours - "CMYK" or "Black & White"
 * @property {string} gsm - Paper weight (e.g., "150gsm")
 * @property {number} qty - Quantity
 * @property {string} [startDate] - Start date in "24-Nov-2025" format
 */

/**
 * Fetch print pricing from calculator API
 *
 * @param {PrintSpecs} specs - Print specifications
 * @returns {Promise<string|null>} Price string "$350.00" or null on failure
 *
 * @example
 * const price = await fetchPrintPrice({
 *   paperSize: 'A5',
 *   pagination: 'Double Sided',
 *   colours: 'CMYK',
 *   gsm: '150gsm',
 *   qty: 4895,
 *   startDate: '24-Nov-2025'
 * });
 * // Returns "$350.00" or null
 */
export async function fetchPrintPrice(specs) {
    if (!API_BASE) {
        console.error('[PrintAPI] VITE_API_GATEWAY_URL not configured');
        return null;
    }

    try {
        const params = new URLSearchParams({
            paper_size: specs.paperSize,
            pagination: specs.pagination,
            colours: specs.colours,
            gsm: specs.gsm,
            qty: String(specs.qty),
        });

        // Only add start_date if provided
        if (specs.startDate) {
            params.set('start_date', specs.startDate);
        }

        const url = `${API_BASE}/calculator/v1/quote-print?${params}`;

        if (import.meta.env.DEV) {
            console.log('[PrintAPI] Fetching price:', url);
        }

        const response = await fetch(url, {
            method: 'GET',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();

        if (import.meta.env.DEV) {
            console.log('[PrintAPI] Response:', data);
        }

        // Extract price from response structure
        const price = data.printcalc?.prices?.[0]?.total;

        if (!price && price !== 0) {
            throw new Error('No pricing data in response');
        }

        // Format as currency string
        const formattedPrice = formatCurrency(parseFloat(price));

        return formattedPrice;
    } catch (error) {
        Sentry.captureException(error, {
            tags: { feature: 'print-upsell', api: 'pricing' },
            extra: { specs },
        });

        console.error('[PrintAPI] Pricing fetch failed:', error);
        return null; // Graceful degradation
    }
}

/**
 * Add print service to quote
 *
 * @param {string} quoteReference - Quote reference ID
 * @param {PrintSpecs} printSpecs - Print specifications
 * @returns {Promise<Object>} Updated quote data
 * @throws {Error} On API failure
 *
 * PLACEHOLDER - API endpoint to be provided:
 * POST {VITE_API_GATEWAY_URL}/quotes/{reference}/add-print
 * Request body: { paperSize, pagination, colours, gsm, qty }
 * Response: Updated quote object with Print class added
 *
 * TODO: Implement when quote update API endpoint is provided
 */
export async function addPrintToQuote(quoteReference, printSpecs) {
    if (!API_BASE) {
        throw new Error('VITE_API_GATEWAY_URL not configured');
    }

    // PLACEHOLDER: Replace with actual API endpoint when available
    // const url = `${API_BASE}/quotes/${quoteReference}/add-print`;
    //
    // const response = await fetch(url, {
    //     method: 'POST',
    //     headers: {
    //         'Accept': 'application/json',
    //         'Content-Type': 'application/json',
    //     },
    //     body: JSON.stringify({
    //         paperSize: printSpecs.paperSize,
    //         pagination: printSpecs.pagination,
    //         colours: printSpecs.colours,
    //         gsm: printSpecs.gsm,
    //         qty: printSpecs.qty,
    //     }),
    // });
    //
    // if (!response.ok) {
    //     throw new Error(`Failed to add print: HTTP ${response.status}`);
    // }
    //
    // return await response.json();

    // TODO: Remove this placeholder implementation when API is available
    // For now, simulate success after a delay
    if (import.meta.env.DEV) {
        console.log('[PrintAPI] addPrintToQuote called (placeholder):', {
            quoteReference,
            printSpecs,
        });
        console.warn(
            '[PrintAPI] Quote update API endpoint not yet implemented. Using placeholder response.'
        );
    }

    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 1000));

    // For development, return a success indicator
    // The parent component will need to reload quote data
    return {
        success: true,
        message: 'Print service added (placeholder)',
        quoteReference,
    };
}
